import { ImageMode, ImageModeUtil } from './enums';
import { buildPalette, utils, applyPalette } from './image-q/image-q';
import { round_half_up, str_pad, dechex } from './helpers';

export interface ConverterOptions {
  dith?: boolean;
  cf: ImageMode;
  binaryFormat: ImageMode;
  swapEndian: boolean;
  outName: string;
  useLegacyFooterOrder?: boolean;
}
class Converter {
  w = 0; /*Image width*/
  h = 0; /*Image height*/
  raw_len = 0; /* RAW image data size */
  cf: ImageMode; /*Color format*/
  alpha = false; /*Add alpha byte or not*/
  chroma = false; /*Chroma keyed?*/
  d_out!: Array<number>; /*Output data (result)*/
  imageData: Array<number> | Uint8Array; /* Input image data */
  options: ConverterOptions;

  /*Helper variables*/
  r_act!: number;
  b_act!: number;
  g_act!: number;

  /*For dithering*/
  r_earr: Array<number>; /*Classification error for next row of pixels*/
  g_earr: Array<number>;
  b_earr: Array<number>;

  r_nerr: number; /*Classification error for next pixel*/
  g_nerr: number;
  b_nerr: number;

  /* Current pass being made */
  pass: number;

  constructor(
    w: number,
    h: number,
    imageData: number[] | Uint8Array,
    alpha: boolean,
    options: Partial<ConverterOptions>
  ) {
    this.w = w;
    this.h = h;
    this.imageData = imageData;
    this.r_earr = []; /*Classification error for next row of pixels*/
    this.g_earr = [];
    this.b_earr = [];

    if (options.dith) {
      for (let i = 0; i < this.w + 2; ++i) {
        this.r_earr[i] = 0;
        this.g_earr[i] = 0;
        this.b_earr[i] = 0;
      }
    }

    this.r_nerr = 0; /*Classification error for next pixel*/
    this.g_nerr = 0;
    this.b_nerr = 0;
    this.pass = 0;
    this.cf = options.cf as ImageMode;
    this.alpha = alpha;
    this.options = options as ConverterOptions;
  }

  /**
   * Get the number of passes being made over an image to output it.
   */
  getNumPasses(): number {
    if (this.cf == ImageMode.CF_RGB565A8) return 2;
    else return 1;
  }

  async convert(): Promise<string | ArrayBuffer> {
    if (this.cf == ImageMode.CF_RAW || this.cf == ImageMode.CF_RAW_ALPHA) {
      const d_array = Array.from(this.imageData as Uint8Array);
      this.raw_len = d_array.length;
      const indent = this.options.useLegacyFooterOrder ? '  ' : '    ';
      const numValuesPerRow = this.options.useLegacyFooterOrder ? 15 : 12;
      let str =
        '\n' +
        indent +
        d_array
          .map(
            (val, i) =>
              '0x' +
              str_pad(dechex(val), 2, '0', true) +
              (i % (numValuesPerRow + 1) == numValuesPerRow
                ? ', \n' + indent
                : ', ')
          )
          .join('');
      str = str.substr(0, str.length - 2);
      return str;
    }
    let palette_size = 0;
    if (this.cf == ImageMode.CF_INDEXED_1_BIT) {
      palette_size = 2;
    } else if (this.cf == ImageMode.CF_INDEXED_2_BIT) {
      palette_size = 4;
    } else if (this.cf == ImageMode.CF_INDEXED_4_BIT) {
      palette_size = 16;
    } else if (this.cf == ImageMode.CF_INDEXED_8_BIT) {
      palette_size = 256;
    }
    this.d_out = [];

    if (palette_size) {
      const pointContainer = utils.PointContainer.fromUint8Array(
        this.imageData,
        this.w,
        this.h
      );
      const palette = await buildPalette([pointContainer], {
        colors: palette_size // optional
      });
      const color_arr = this.d_out;
      const palettePointArray = palette.getPointContainer().getPointArray();
      const paletteColors = palettePointArray.map((point) => {
        return point.uint32;
      });
      for (let i = 0; i < palette_size; i++) {
        if (i < palettePointArray.length) {
          color_arr.push(
            palettePointArray[i].b,
            palettePointArray[i].g,
            palettePointArray[i].r,
            palettePointArray[i].a
          );
        } else {
          color_arr.push(0, 0, 0, 0);
        }
      }

      const outPointContainer = await applyPalette(pointContainer, palette, {});
      const outPointArray = outPointContainer.getPointArray();
      this.imageData = [];
      outPointArray.forEach((point, arrayIndex) => {
        const index = paletteColors.indexOf(point.uint32);
        if (index == -1) throw new Error('Unknown color??');
        (this.imageData as Array<number>).push(index);
      });
    }

    const needsFormatSwap = ImageModeUtil.isTrueColor(this.cf);
    if (needsFormatSwap) {
      this.cf = this.options.binaryFormat;
    }

    for (this.pass = 0; this.pass < this.getNumPasses(); this.pass++) {
      /*Convert all the pixels*/
      for (let y = 0; y < this.h; y++) {
        this.dith_reset();

        for (let x = 0; x < this.w; ++x) {
          this.conv_px(x, y);
        }
      }
    }

    // 与固件调整确认不加headerbit
    const finalBinary = new Uint8Array(this.d_out.length);

    for (let i = 0; i < this.d_out.length; i++) {
      // 与固件调整确认finalBinary为i而不是i+1
      finalBinary[i] = this.d_out[i];
    }
    return finalBinary.buffer;
  }

  private conv_px(x: number, y: number) {
    function array_push<T>(arr: Array<T>, v: T) {
      arr.push(v);
    }
    function isset(val: any): boolean {
      return typeof val != 'undefined' && val != undefined;
    }
    const startIndex = (y * this.w + x) * 4;
    let a;
    if (this.alpha) {
      a = this.imageData[startIndex + 3];
    } else {
      a = 0xff;
    }
    const r = this.imageData[startIndex];
    const g = this.imageData[startIndex + 1];
    const b = this.imageData[startIndex + 2];

    const c = this.imageData[y * this.w + x];

    if (
      this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8565 ||
      this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8565_RBSWAP ||
      this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8332 ||
      this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8888 ||
      this.cf == ImageMode.CF_RGB565A8
    ) {
      /* Populate r_act, g_act, b_act */
      this.dith_next(r, g, b, x);
    }

    if (this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8332) {
      const c8 = this.r_act | (this.g_act >> 3) | (this.b_act >> 6); //RGB332
      array_push(this.d_out, c8);
      if (this.alpha) array_push(this.d_out, a);
    } else if (this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8565) {
      const c16 = (this.r_act << 8) | (this.g_act << 3) | (this.b_act >> 3); //RGR565
      array_push(this.d_out, c16 & 0xff);
      array_push(this.d_out, (c16 >> 8) & 0xff);
      if (this.alpha) array_push(this.d_out, a);
    } else if (this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8565_RBSWAP) {
      const c16 = (this.r_act << 8) | (this.g_act << 3) | (this.b_act >> 3); //RGR565
      array_push(this.d_out, (c16 >> 8) & 0xff);
      array_push(this.d_out, c16 & 0xff);
      if (this.alpha) array_push(this.d_out, a);
    } else if (this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8888) {
      array_push(this.d_out, this.b_act);
      array_push(this.d_out, this.g_act);
      array_push(this.d_out, this.r_act);
      array_push(this.d_out, a);
    } else if (this.cf == ImageMode.CF_RGB565A8) {
      if (this.pass == 0) {
        const c16 = (this.r_act << 8) | (this.g_act << 3) | (this.b_act >> 3); //RGR565
        array_push(this.d_out, c16 & 0xff);
        array_push(this.d_out, (c16 >> 8) & 0xff);
      } else if (this.pass == 1) {
        if (this.alpha) array_push(this.d_out, a);
      }
    } else if (this.cf == ImageMode.CF_ALPHA_1_BIT) {
      let w = this.w >> 3;
      if (this.w & 0x07) w++;
      const p = w * y + (x >> 3);
      if (!isset(this.d_out[p])) {
        this.d_out[p] = 0; /*Clear the bits first*/
      }
      if (a > 0x80) {
        this.d_out[p] |= 1 << (7 - (x & 0x7));
      }
    } else if (this.cf == ImageMode.CF_ALPHA_2_BIT) {
      let w = this.w >> 2;
      if (this.w & 0x03) w++;

      const p = w * y + (x >> 2);
      if (!isset(this.d_out[p])) this.d_out[p] = 0; /*Clear the bits first*/
      this.d_out[p] |= (a >> 6) << (6 - (x & 0x3) * 2);
    } else if (this.cf == ImageMode.CF_ALPHA_4_BIT) {
      let w = this.w >> 1;
      if (this.w & 0x01) w++;

      const p = w * y + (x >> 1);
      if (!isset(this.d_out[p])) this.d_out[p] = 0; /*Clear the bits first*/
      this.d_out[p] |= (a >> 4) << (4 - (x & 0x1) * 4);
    } else if (this.cf == ImageMode.CF_ALPHA_8_BIT) {
      const p = this.w * y + x;
      this.d_out[p] = a;
    } else if (this.cf == ImageMode.CF_INDEXED_1_BIT) {
      let w = this.w >> 3;
      if (this.w & 0x07) w++;

      const p = w * y + (x >> 3) + 8; // +8 for the palette
      if (!isset(this.d_out[p])) this.d_out[p] = 0; //Clear the bits first
      this.d_out[p] |= (c & 0x1) << (7 - (x & 0x7));
    } else if (this.cf == ImageMode.CF_INDEXED_2_BIT) {
      let w = this.w >> 2;
      if (this.w & 0x03) w++;

      const p = w * y + (x >> 2) + 16; // +16 for the palette
      if (!isset(this.d_out[p])) this.d_out[p] = 0; // Clear the bits first
      this.d_out[p] |= (c & 0x3) << (6 - (x & 0x3) * 2);
    } else if (this.cf == ImageMode.CF_INDEXED_4_BIT) {
      let w = this.w >> 1;
      if (this.w & 0x01) w++;

      const p = w * y + (x >> 1) + 64; // +64 for the palette
      if (!isset(this.d_out[p])) this.d_out[p] = 0; // Clear the bits first
      this.d_out[p] |= (c & 0xf) << (4 - (x & 0x1) * 4);
    } else if (this.cf == ImageMode.CF_INDEXED_8_BIT) {
      const p = this.w * y + x + 1024; // +1024 for the palette
      this.d_out[p] = c & 0xff;
    }
  }

  dith_reset() {
    if (this.options.dith) {
      this.r_nerr = 0;
      this.g_nerr = 0;
      this.b_nerr = 0;
    }
  }

  dith_next(r: any, g: any, b: any, x: any) {
    if (this.options.dith) {
      this.r_act = r + this.r_nerr + this.r_earr[x + 1];
      this.r_earr[x + 1] = 0;

      this.g_act = g + this.g_nerr + this.g_earr[x + 1];
      this.g_earr[x + 1] = 0;

      this.b_act = b + this.b_nerr + this.b_earr[x + 1];
      this.b_earr[x + 1] = 0;

      if (this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8332) {
        this.r_act = this.classify_pixel(this.r_act, 3);
        this.g_act = this.classify_pixel(this.g_act, 3);
        this.b_act = this.classify_pixel(this.b_act, 2);

        if (this.r_act > 0xe0) this.r_act = 0xe0;
        if (this.g_act > 0xe0) this.g_act = 0xe0;
        if (this.b_act > 0xc0) this.b_act = 0xc0;
      } else if (
        this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8565 ||
        this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8565_RBSWAP
      ) {
        this.r_act = this.classify_pixel(this.r_act, 5);
        this.g_act = this.classify_pixel(this.g_act, 6);
        this.b_act = this.classify_pixel(this.b_act, 5);

        if (this.r_act > 0xf8) this.r_act = 0xf8;
        if (this.g_act > 0xfc) this.g_act = 0xfc;
        if (this.b_act > 0xf8) this.b_act = 0xf8;
      } else if (this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8888) {
        this.r_act = this.classify_pixel(this.r_act, 8);
        this.g_act = this.classify_pixel(this.g_act, 8);
        this.b_act = this.classify_pixel(this.b_act, 8);

        if (this.r_act > 0xff) this.r_act = 0xff;
        if (this.g_act > 0xff) this.g_act = 0xff;
        if (this.b_act > 0xff) this.b_act = 0xff;
      }

      this.r_nerr = r - this.r_act;
      this.g_nerr = g - this.g_act;
      this.b_nerr = b - this.b_act;

      this.r_nerr = round_half_up((7 * this.r_nerr) / 16);
      this.g_nerr = round_half_up((7 * this.g_nerr) / 16);
      this.b_nerr = round_half_up((7 * this.b_nerr) / 16);

      this.r_earr[x] += round_half_up((3 * this.r_nerr) / 16);
      this.g_earr[x] += round_half_up((3 * this.g_nerr) / 16);
      this.b_earr[x] += round_half_up((3 * this.b_nerr) / 16);

      this.r_earr[x + 1] += round_half_up((5 * this.r_nerr) / 16);
      this.g_earr[x + 1] += round_half_up((5 * this.g_nerr) / 16);
      this.b_earr[x + 1] += round_half_up((5 * this.b_nerr) / 16);

      this.r_earr[x + 2] += round_half_up(this.r_nerr / 16);
      this.g_earr[x + 2] += round_half_up(this.g_nerr / 16);
      this.b_earr[x + 2] += round_half_up(this.b_nerr / 16);
    } else {
      if (this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8332) {
        this.r_act = this.classify_pixel(r, 3);
        this.g_act = this.classify_pixel(g, 3);
        this.b_act = this.classify_pixel(b, 2);

        if (this.r_act > 0xe0) this.r_act = 0xe0;
        if (this.g_act > 0xe0) this.g_act = 0xe0;
        if (this.b_act > 0xc0) this.b_act = 0xc0;
      } else if (
        this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8565 ||
        this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8565_RBSWAP ||
        this.cf == ImageMode.CF_RGB565A8
      ) {
        this.r_act = this.classify_pixel(r, 5);
        this.g_act = this.classify_pixel(g, 6);
        this.b_act = this.classify_pixel(b, 5);

        if (this.r_act > 0xf8) this.r_act = 0xf8;
        if (this.g_act > 0xfc) this.g_act = 0xfc;
        if (this.b_act > 0xf8) this.b_act = 0xf8;
      } else if (this.cf == ImageMode.ICF_TRUE_COLOR_ARGB8888) {
        this.r_act = this.classify_pixel(r, 8);
        this.g_act = this.classify_pixel(g, 8);
        this.b_act = this.classify_pixel(b, 8);

        if (this.r_act > 0xff) this.r_act = 0xff;
        if (this.g_act > 0xff) this.g_act = 0xff;
        if (this.b_act > 0xff) this.b_act = 0xff;
      }
    }
  }

  classify_pixel(value: number, bits: number) {
    const tmp = 1 << (8 - bits);
    let val = Math.round(value / tmp) * tmp;
    if (val < 0) val = 0;
    return val;
  }
}

export function isNotRaw(options: { cf: ImageMode }): boolean {
  return (
    options.cf != ImageMode.CF_RAW && options.cf != ImageMode.CF_RAW_ALPHA
  ); /* && options.cf != ImageMode.CF_RAW_CHROMA; */
}

async function convertImageBlob(
  img: number[] | Uint8Array,
  width: number,
  height: number,
  options: Partial<ConverterOptions>
): Promise<ArrayBuffer> {
  const alpha =
    options.cf == ImageMode.CF_TRUE_COLOR_ALPHA ||
    options.cf == ImageMode.CF_ALPHA_1_BIT ||
    options.cf == ImageMode.CF_ALPHA_2_BIT ||
    options.cf == ImageMode.CF_ALPHA_4_BIT ||
    options.cf == ImageMode.CF_ALPHA_8_BIT ||
    options.cf == ImageMode.CF_RGB565A8;
  const binaryConv = new Converter(width, height, img, alpha, options);
  const bin_res_blob = (await binaryConv.convert()) as ArrayBuffer;

  return bin_res_blob;
}

export { convertImageBlob, Converter };
